/**
 * Role + never-signed-in badges shared by `/employees` PersonRow and Live roster RosterRow
 * (c21.4 / c21.5) so the copy stays in one place.
 *
 * c21.10: name on its own line; badges underneath. Position is a separate column.
 */

import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { TableHead } from "@/components/ui/table";
import { formatPersonName } from "@/lib/format";
import { isNeverSignedIn, type PersonSummary } from "@/shared/schemas/tenancy";

export function PersonStatusBadges({ person }: { person: PersonSummary }) {
  const { t } = useTranslation();

  return (
    <>
      {person.role === "company_admin" && (
        <Badge variant="outline" data-testid={`person-role-badge-${person.id}`}>
          {t("employees.roleCompanyAdmin")}
        </Badge>
      )}
      {isNeverSignedIn(person) && (
        <Badge variant="secondary" data-testid={`person-never-signed-in-${person.id}`}>
          {t("employees.neverSignedInInvited", { count: person.inviteCount })}
        </Badge>
      )}
    </>
  );
}

export function EmployeeNameCell({
  person,
  showArchived = false,
}: {
  person: PersonSummary;
  showArchived?: boolean;
}) {
  const { t } = useTranslation();
  const fullName = formatPersonName(person);
  const archived = showArchived && person.archived;

  return (
    <div className="flex min-w-0 flex-col gap-1" data-testid={`employee-name-cell-${person.id}`}>
      <span className="font-medium" data-testid={`employee-name-${person.id}`}>
        {fullName}
      </span>
      <div className="flex flex-wrap items-center gap-2">
        <PersonStatusBadges person={person} />
        {archived && <Badge variant="secondary">{t("employees.archivedBadge")}</Badge>}
      </div>
    </div>
  );
}

/** c21.11 — Std./Tage (or Hours/Days) stacked over Woche (week). */
export function TwoLineTableHead({
  line1,
  line2,
  className,
  testId,
}: {
  line1: string;
  line2: string;
  className?: string;
  testId?: string;
}) {
  return (
    <TableHead className={className} data-testid={testId}>
      <span className="flex flex-col items-end leading-tight">
        <span>{line1}</span>
        <span>{line2}</span>
      </span>
    </TableHead>
  );
}
